// frontend/src/pages/Cidades.js
import React, { useEffect, useState } from "react";
import "./Cidades.css";
import { jwtDecode } from "jwt-decode";
import { copiarTexto } from "../utils/clipboard";
import ThemeProvider from "../theme/TemaMuiProvider";
import { Box, Button, TextField, TablePagination, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import muiTheme from "../theme/muiTheme";

function Cidades() {
  const [cidades, setCidades] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [cidadesIBGE, setCidadesIBGE] = useState([]);
  const [novaCidade, setNovaCidade] = useState("");
  const [editandoId, setEditandoId] = useState(null);
  const [nomeEditado, setNomeEditado] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [cidadesPorPagina, setCidadesPorPagina] = useState(5);

  // ✅ minimizar/expandir grid (persistido)
  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const saved = localStorage.getItem("cidadesGridMinimizado");
    return saved ? saved === "1" : false;
  });

  useEffect(() => {
    localStorage.setItem("cidadesGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  // =======================
  // SISTEMA DE PERMISSÕES
  // =======================
  const token = localStorage.getItem("token");
  let tipoUsuario = "";

  if (token) {
    const decoded = jwtDecode(token);

    tipoUsuario = decoded.tipo
      ?.toString()
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeEditar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);

  // =======================
  // Buscar cidades
  // =======================
  useEffect(() => {
    const tk = localStorage.getItem("token");
    fetch("/api/cidades", {
      headers: { Authorization: `Bearer ${tk}` },
    })
      .then((res) => res.json())
      .then((data) => {
        const lista = Array.isArray(data) ? data : Array.isArray(data.cidades) ? data.cidades : [];
        setCidades(lista);
      })
      .catch((err) => console.error("Erro ao buscar cidades:", err));
  }, []);

  // =======================
  // Cidades do IBGE
  // =======================
  useEffect(() => {
    fetch("https://servicodados.ibge.gov.br/api/v1/localidades/municipios")
      .then((res) => res.json())
      .then((data) => {
        const nomes = data.map((c) => c.nome.toLowerCase());
        setCidadesIBGE(nomes);
      })
      .catch((err) => console.error("Erro IBGE:", err));
  }, []);

  // =======================
  // Criar cidade
  // =======================
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!podeCadastrar) {
      alert("Você não tem permissão para cadastrar cidades.");
      return;
    }

    const tk = localStorage.getItem("token");

    const digitado = novaCidade.trim();
    const nomeUpper = digitado.toUpperCase();

    const nomeFormatado = digitado.toLowerCase();

    if (cidades.some((c) => c.nome.trim().toLowerCase() === nomeFormatado)) {
      alert("Cidade já cadastrada.");
      return;
    }

    if (!cidadesIBGE.includes(nomeFormatado)) {
      alert("Cidade inválida segundo o IBGE.");
      return;
    }

    fetch("/api/cidades", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tk}`,
      },
      body: JSON.stringify({ nome: nomeUpper }),
    })
      .then(async (res) => {
        if (res.status === 403) {
          alert("Sem permissão para cadastrar.");
          return;
        }
        const data = await res.json();
        if (data?.id) {
          setCidades((prev) => [
            {
              ...data,
              nome: (data.nome ?? nomeUpper).toUpperCase(),
            },
            ...prev,
          ]);

          setPaginaAtual(1);

          setNovaCidade("");
        }
      })
      .catch((err) => console.error("Erro ao cadastrar:", err));
  };

  // =======================
  // Entrar em modo edição
  // =======================
  const handleEdit = (cidade) => {
    if (!podeEditar) {
      alert("Você não tem permissão para editar.");
      return;
    }
    setEditandoId(cidade.id);
    setNomeEditado(cidade.nome);
  };

  // =======================
  // Salvar edição
  // =======================
  const handleSaveEdit = (id) => {
    if (!podeEditar) {
      alert("Você não tem permissão para editar.");
      return;
    }

    const tk = localStorage.getItem("token");
    const novoNomeUpper = nomeEditado.trim().toUpperCase();
    fetch(`/api/cidades/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tk}`,
      },
      body: JSON.stringify({ nome: novoNomeUpper }),
    })
      .then(async (res) => {
        if (res.status === 403) {
          alert("Sem permissão para editar.");
          return;
        }

        setCidades((prev) => prev.map((c) => (c.id === id ? { ...c, nome: novoNomeUpper } : c)));
        setEditandoId(null);
        setNomeEditado("");
      })
      .catch((err) => console.error("Erro ao editar cidade:", err));
  };

  // =======================
  // Excluir
  // =======================
  const handleDelete = (id) => {
    if (!podeExcluir) {
      alert("Você não tem permissão para excluir.");
      return;
    }

    const confirmar = window.confirm("Deseja realmente excluir esta cidade?");
    if (!confirmar) return;

    const tk = localStorage.getItem("token");
    fetch(`/api/cidades/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${tk}` },
    })
      .then(async (res) => {
        if (res.status === 403) {
          alert("Sem permissão para excluir.");
          return;
        }
        setCidades((prev) => prev.filter((c) => c.id !== id));
      })
      .catch((err) => console.error("Erro ao excluir cidade:", err));
  };

  // =======================
  // Filtrar cidades
  // =======================
  const cidadesFiltradas = cidades.filter((c) => c.nome.toLowerCase().includes(filtro.toLowerCase()));

  const indexInicial = (paginaAtual - 1) * cidadesPorPagina;
  const indexFinal = indexInicial + cidadesPorPagina;
  const cidadesPaginadas = cidadesFiltradas.slice(indexInicial, indexFinal);
  const totalPaginas = Math.ceil(cidadesFiltradas.length / cidadesPorPagina) || 1;

  useEffect(() => {
    if (paginaAtual > totalPaginas) setPaginaAtual(1);
  }, [paginaAtual, totalPaginas]);

  // =======================
  // Copiar lista (usa a lista filtrada inteira)
  // =======================
  const handleCopiar = async () => {
    const texto = (cidadesFiltradas || [])
      .map((c) => c?.nome ?? "")
      .filter(Boolean)
      .join(", ");

    if (!texto.trim()) {
      alert("Nada para copiar (lista vazia).");
      return;
    }

    const ok = await copiarTexto(texto);
    alert(ok ? "Cidades copiadas!" : "Não foi possível copiar automaticamente.");
  };

  // =======================
  // CSV (exporta lista filtrada inteira)
  // =======================
  const handleCSV = () => {
    const BOM = "\uFEFF";
    const header = ["Cidade"];

    const csvEscape = (value) => {
      const s = String(value ?? "");
      if (/[;"\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const linhas = (cidadesFiltradas || []).map((c) => [c.nome ?? ""].map(csvEscape));

    const csv = [header.map(csvEscape), ...linhas].map((l) => l.join(";")).join("\n");

    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "cidades.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="cidade-container">
        <style>{`
        .campo-formulario-padrao,
        .campo-formulario-padrao:focus,
        .campo-formulario-padrao:active,
        .campo-formulario-padrao:read-only,
        .campo-formulario-padrao:disabled {
          background: transparent !important;
          background-color: transparent !important;
          color: var(--ig-t-ffffff) !important;
          box-shadow: none !important;
          -webkit-text-fill-color: var(--ig-t-ffffff) !important;
          opacity: 1 !important;
        }

        .campo-formulario-padrao::placeholder {
          color: var(--ig-t-255-255-255-a072) !important;
          -webkit-text-fill-color: var(--ig-t-255-255-255-a072) !important;
        }

        .campo-formulario-padrao::-webkit-calendar-picker-indicator {
          filter: var(--ig-x-11pexw5);
          opacity: 0.9;
          cursor: pointer;
        }
      `}</style>

        <h2>Cidades</h2>

        {/* FILTRO + BOTÕES (sempre visíveis) */}
        <div className="bloco-linha">
          <TextField
            label="Buscar Cidades"
            value={filtro}
            onChange={(e) => {
              setFiltro(e.target.value);
              setPaginaAtual(1);
            }}
            fullWidth
            size="small"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon fontSize="small" sx={{ color: "var(--ig-t-9ca3af)" }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 52,
                display: "flex",
                alignItems: "center",
              },
              "& .MuiOutlinedInput-input": {
                padding: "14px 14px",
              },
              "& .MuiInputLabel-root": {
                transform: "translate(14px, 15px) scale(1)",
              },
              "& .MuiInputLabel-shrink": {
                transform: "translate(14px, -9px) scale(0.75)",
              },
            }}
          />

          <div className="filtro-botoes">
            <Button
              size="small"
              variant="outlined"
              type="button"
              onClick={() => {
                setFiltro("");
                setPaginaAtual(1);
              }}
              sx={{
                height: 52,
                color: "#2196f3",
                borderColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Limpar Filtro
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={handleCopiar}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              <i className="fas fa-copy" style={{ marginRight: 6 }}></i>
              Copiar
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={handleCSV}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              CSV
            </Button>
          </div>
        </div>

        {/* ✅ BOTÃO MINIMIZAR/EXPANDIR (sempre visível) */}
        <div className="grid-toggle-area right">
          {!gridMinimizado ? (
            <button
              type="button"
              className="btn-grid-toggle small danger"
              onClick={() => setGridMinimizado(true)}
              title="Minimizar o Grid"
              aria-label="Minimizar o Grid"
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              className="btn-grid-toggle small outline"
              onClick={() => setGridMinimizado(false)}
              title="Expandir o Grid"
              aria-label="Expandir o Grid"
            >
              ↗ Expandir
            </button>
          )}
        </div>

        {/* GRID (tabela + paginação) */}
        {!gridMinimizado && (
          <div className="bloco-lista">
            {/* TABELA */}
            <table>
              <thead>
                <tr>
                  <th>Cidade</th>
                  <th style={{ width: "300px", textAlign: "center" }}>Ações</th>
                </tr>
              </thead>

              <tbody>
                {cidadesPaginadas.map((cidade) => (
                  <tr key={cidade.id}>
                    <td className="linha-cidade">
                      {editandoId === cidade.id ? (
                        <input value={nomeEditado} onChange={(e) => setNomeEditado(e.target.value.toUpperCase())} className="campo-formulario-padrao" />
                      ) : (
                        cidade.nome
                      )}
                    </td>

                    <td
                      className="celula-acoes"
                      style={{
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        {editandoId === cidade.id ? (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<i className="fas fa-save" />}
                            onClick={() => handleSaveEdit(cidade.id)}
                            sx={{
                              minWidth: 110,
                              height: 38,
                              textTransform: "none",
                              fontWeight: 600,
                              background: "#2e7d32",
                              "&:hover": {
                                background: "#1b5e20",
                              },
                            }}
                          >
                            Salvar
                          </Button>
                        ) : (
                          <>
                            {podeEditar && (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<i className="fas fa-pen-to-square" />}
                                onClick={() => handleEdit(cidade)}
                                sx={{
                                  minWidth: 110,
                                  height: 38,
                                  textTransform: "none",
                                  fontWeight: 600,
                                  background: "#1998a8",
                                  "&:hover": {
                                    background: "#177f8d",
                                  },
                                }}
                              >
                                Editar
                              </Button>
                            )}

                            {podeExcluir && (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<i className="fas fa-trash" />}
                                onClick={() => handleDelete(cidade.id)}
                                sx={{
                                  minWidth: 110,
                                  height: 38,
                                  textTransform: "none",
                                  fontWeight: 600,
                                  background: "#d32f2f",
                                  "&:hover": {
                                    background: "#b71c1c",
                                  },
                                }}
                              >
                                Excluir
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* PAGINAÇÃO */}
            <TablePagination
              component="div"
              count={cidadesFiltradas.length}
              page={paginaAtual - 1}
              onPageChange={(e, newPage) => setPaginaAtual(newPage + 1)}
              rowsPerPage={cidadesPorPagina}
              rowsPerPageOptions={[5, 10, 25]}
              onRowsPerPageChange={(e) => {
                setCidadesPorPagina(parseInt(e.target.value, 10));
                setPaginaAtual(1);
              }}
              labelRowsPerPage="Cidades por página:"
              sx={{
                color: "var(--ig-t-ffffff)",

                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 0,
                  paddingRight: 0,
                  minHeight: 58,
                },

                "& .MuiTablePagination-selectLabel": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiTablePagination-displayedRows": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiSvgIcon-root": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiIconButton-root": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiSelect-select": {
                  color: "var(--ig-t-ffffff)",
                },
              }}
            />
          </div>
        )}

        {/* AVISO DE PERMISSÃO */}
        {!podeCadastrar && (
          <div className="aviso-permissao-container">
            <p className="aviso-permissao">🔒 Você possui acesso apenas de visualização.</p>
          </div>
        )}

        {/* FORMULÁRIO */}
        {podeCadastrar && (
          <div className="bloco-formulario">
            <form onSubmit={handleSubmit} className="cidade-form">
              <label htmlFor="cidade">Cidade</label>
              <input
                id="cidade"
                value={novaCidade}
                onChange={(e) => setNovaCidade(e.target.value.toUpperCase())}
                placeholder="Digite o nome da cidade"
                required
                className="campo-formulario-padrao"
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 2,
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<i className="fas fa-save" />}
                  sx={{
                    width: 240,
                    minWidth: 240,
                    maxWidth: 240,

                    height: 52,
                    minHeight: 52,

                    backgroundColor: "#2e7d32",

                    color: "#ffffff",

                    borderRadius: "6px",

                    textTransform: "none",
                    fontSize: "16px",
                    fontWeight: 700,

                    "&:hover": {
                      backgroundColor: "#1b5e20",
                    },
                  }}
                >
                  Cadastrar
                </Button>
              </Box>
            </form>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}

export default Cidades;
